// RecipeEditPage.tsx — create or edit a recipe you own.
//
// The same page serves both: with no :id it creates, with one it loads the
// recipe and refuses to open unless the server says canEdit. Ownership is
// re-checked server-side on save, so this is a UX guard, not the security
// boundary.
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Table from 'react-bootstrap/Table'
import { useConfirm } from 'common/ui/ConfirmDialog'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Badge from 'react-bootstrap/Badge'
import {
    BsSave, BsTrash, BsPlusLg, BsDownload, BsArrowLeft, BsX, BsMagic,
} from 'react-icons/bs'
import Text from 'common/ui/Text'
import Dropdown from 'common/ui/Dropdown'
import {
    type Recipe, type Ingredient, type Step, type StepUse, type Difficulty,
    type Confidence, type ImportMethod,
    emptyRecipe, getRecipe, createRecipe, updateRecipe, deleteRecipe,
    importRecipe, measureQuantity, allocateStepUses, totalMass, ApiError,
} from '../api'
import { formatGrams, gramsLabel } from '../format'
import { parseMass, roundGrams } from '../quantity'

const difficulties: Difficulty[] = ['beginner', 'intermediate', 'advanced', 'expert']

// confidenceBadge describes how a parsed mass was arrived at.
//
// This is the whole reason the parse result is previewed rather than dropped
// straight into the form. "240 g" reads the same whether a national food table
// said so or a model guessed, and the difference is exactly what an author needs
// to see before accepting it.
const confidenceBadge: Record<Confidence, { bg: string; label: string; hint: string }> = {
    exact: {
        bg: 'success', label: 'exact',
        hint: 'The text already gave a weight. Only unit arithmetic was applied.',
    },
    estimated: {
        bg: 'info', label: 'from tables',
        hint: 'Converted from volume or count using a published density. Check the basis names the food you meant.',
    },
    nominal: {
        bg: 'secondary', label: 'a pinch',
        hint: 'A pinch or a dash of something no table covers, taken as the conventional amount. '
            + 'Neutral rather than a warning: every possible answer here is under half a gram.',
    },
    model: {
        bg: 'warning', label: 'guessed',
        hint: 'No published table covers this ingredient, so a language model guessed the weight. Weigh it before trusting it.',
    },
    unknown: {
        bg: 'danger', label: 'no mass',
        hint: 'Nothing could be derived. You need to weigh this one and fill it in.',
    },
}

// MassInfo is where one ingredient's mass came from. An import supplies one per
// ingredient; settling the quantity field supplies one for the row it settled;
// and a conversion that found nothing supplies the reason, which is the most
// useful of the three.
//
// None of it belongs to the recipe. It accounts for the number on screen, which
// is what an author needs while checking it and which would be a stale claim the
// moment they change it — so it lives beside the form, for as long as the page.
interface MassInfo {
    basis?: string
    confidence: Confidence
    note?: string
}

interface QuantityFieldProps {
    /** The mass the recipe records for this ingredient, in grams. */
    massG: number
    /** What this field is, for screen readers — the value alone says nothing. */
    label: string
    /** Resolves the typed text. Awaited, so the field can say it is working. */
    onSettle: (text: string) => Promise<void>
}

// QuantityField takes an amount in whatever unit the cook has it in, and settles
// to the grams the recipe stores.
//
// The draft is why this is a component and not an input. A half-typed quantity
// is not a quantity — "", "1.", "1 c" on the way to "1 cup" are all legal
// keystrokes — and reformatting them as they are typed fights the cursor. So the
// field shows raw text while it is being edited and the settled mass the rest of
// the time, and leaving it is what converts.
const QuantityField = ({ massG, label, onSettle }: QuantityFieldProps) => {
    const [draft, setDraft] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    // Escape has to beat the blur it causes: .blur() dispatches synchronously,
    // before React re-renders, so the blur handler would still be holding the
    // draft it is meant to be discarding.
    const cancelled = useRef(false)

    const settled = massG > 0 ? gramsLabel(massG) : ''

    // The typed text stays on screen until the answer arrives. A conversion that
    // has to ask the server takes a moment, and blanking the field back to the
    // old mass in the meantime reads as the edit having been thrown away.
    const finish = async () => {
        if (draft === null) return
        if (cancelled.current || draft.trim() === settled) {
            cancelled.current = false
            setDraft(null)
            return
        }
        setBusy(true)
        try {
            await onSettle(draft)
        } finally {
            setBusy(false)
            setDraft(null)
        }
    }

    return (
        <div className="d-flex align-items-center justify-content-end gap-1">
            {busy && <Spinner animation="border" size="sm" role="status" aria-label="converting" />}
            <Text
                value={draft ?? settled}
                placeholder="250 g"
                aria-label={label}
                title={'The amount in whatever unit you have it in — "250 g", "8 oz", '
                    + '"1 1/2 cups", "3 cloves". A bare number is grams. Anything that is not '
                    + 'already a weight is converted from the reference tables when you leave '
                    + 'the field, and it settles to grams.'}
                onChange={e => setDraft(e.target.value)}
                onBlur={finish}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur()
                    } else if (e.key === 'Escape') {
                        cancelled.current = true
                        ;(e.target as HTMLInputElement).blur()
                    }
                }}
                style={{ textAlign: 'right', width: '100%' }}
            />
        </div>
    )
}

interface StepUseFieldProps {
    /** The ingredient's whole mass, which the typed amount is a share of. */
    totalG: number
    portion: number
    label: string
    onSettle: (portion: number) => void
}

// StepUseField takes what one step uses, in grams, and keeps it as a share of
// that ingredient's total.
//
// Grams because that is what an author has in front of them — the step says
// "125 g of the flour" — and a share because that is what stops the method and
// the ingredient row from ever disagreeing: correct the flour to 260 g and this
// step follows.
//
// Only weights are read here: g, kg, oz, lb, or a bare number of grams. A
// volume would have to be converted against the food, and that conversion
// belongs to the ingredient's own quantity field, where it happens once. A step
// is a share of a mass that field already settled.
const StepUseField = ({ totalG, portion, label, onSettle }: StepUseFieldProps) => {
    const [draft, setDraft] = useState<string | null>(null)
    // Escape has to beat the blur it causes — see QuantityField.
    const cancelled = useRef(false)
    const settled = gramsLabel(totalG * portion)
    const text = draft ?? settled

    const finish = () => {
        if (draft === null) return
        const asked = cancelled.current ? null : parseMass(draft)
        cancelled.current = false
        setDraft(null)
        if (asked !== null && asked > 0 && totalG > 0) onSettle(asked / totalG)
    }

    return (
        <Text
            value={text}
            aria-label={label}
            title={totalG > 0
                ? 'How much of this ingredient the step takes. Kept as a share of the '
                  + 'whole, so it follows if the ingredient\'s own amount changes.'
                : 'This ingredient has no mass yet, so there is no whole for a step to '
                  + 'take a share of. Give it a quantity first.'}
            readOnly={totalG <= 0}
            onChange={e => setDraft(e.target.value)}
            onBlur={finish}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    (e.target as HTMLInputElement).blur()
                } else if (e.key === 'Escape') {
                    cancelled.current = true
                    ;(e.target as HTMLInputElement).blur()
                }
            }}
            // Text sizes itself for a form-sized control; in a chip it is
            // wrapped tighter, so the padding and the width it implies both come
            // down together.
            style={{
                padding: '0.1rem 0.4rem',
                width: `calc(${Math.max(text.length, 4)}ch + 1.2rem)`,
                textAlign: 'right',
            }}
        />
    )
}

const RecipeEditPage = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const editing = Boolean(id)

    const [recipe, setRecipe] = useState<Recipe>(emptyRecipe())
    const [loading, setLoading] = useState(editing)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [importUrl, setImportUrl] = useState('')
    const [importing, setImporting] = useState(false)
    const [useModel, setUseModel] = useState(false)
    const [reviewNotes, setReviewNotes] = useState<string[]>([])
    // How the last import was extracted, and where each mass on screen came
    // from. Neither is part of a saved recipe.
    //
    // massInfo runs parallel to recipe.ingredients and is maintained alongside
    // it: a row's account is dropped when that row is edited, and the list is
    // spliced when one is added or removed, so an index always describes the
    // ingredient it sits next to.
    const [importMethod, setImportMethod] = useState<ImportMethod | null>(null)
    const [massInfo, setMassInfo] = useState<(MassInfo | null)[]>([])
    // What the last allocation of ingredients to steps left for a person to
    // settle. It sits with the procedure rather than in the import alert at the
    // top: it is about the steps, and it can be raised long after any import.
    const [allocating, setAllocating] = useState(false)
    const [amountNotes, setAmountNotes] = useState<string[]>([])

    useEffect(() => {
        if (!editing || !id) return
        getRecipe(id)
            .then(r => {
                if (!r.canEdit) {
                    setError('This recipe belongs to someone else, so it cannot be edited here.')
                } else {
                    setRecipe(r)
                }
            })
            .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load recipe'))
            .finally(() => setLoading(false))
    }, [editing, id])

    // set patches one top-level field.
    const set = useCallback(<K extends keyof Recipe>(key: K, value: Recipe[K]) => {
        setRecipe(prev => ({ ...prev, [key]: value }))
    }, [])

    const runImport = async () => {
        if (!importUrl.trim()) return
        setImporting(true)
        setError('')
        try {
            const draft = await importRecipe(importUrl.trim(), useModel)
            // Only the fields a recipe stores come across. The confidence and
            // basis stay in importedInfo, next to the form rather than in it.
            const incoming: Ingredient[] = (draft.ingredients ?? []).map(p => ({
                name: p.name,
                massG: p.massG,
                notes: p.note || undefined,
                optional: p.optional || undefined,
            }))
            // Merge rather than replace: an import is a starting point, and a
            // field the page did not carry should not blank one already filled.
            setRecipe(prev => ({
                ...prev,
                title: draft.title || prev.title,
                subtitle: draft.subtitle || prev.subtitle,
                category: draft.category || prev.category,
                difficulty: draft.difficulty || prev.difficulty,
                yieldDescription: draft.yieldDescription || prev.yieldDescription,
                totalTimeMin: draft.totalTimeMin || prev.totalTimeMin,
                prepTimeMin: draft.prepTimeMin || prev.prepTimeMin,
                cookTimeMin: draft.cookTimeMin || prev.cookTimeMin,
                equipment: draft.equipment?.length ? draft.equipment : prev.equipment,
                ingredients: incoming.length ? incoming : prev.ingredients,
                steps: draft.steps?.length ? draft.steps : prev.steps,
                notes: draft.notes?.length ? draft.notes : prev.notes,
                sourceUrl: draft.sourceUrl,
            }))
            setImportMethod(draft.method ?? null)
            setMassInfo(incoming.length ? draft.ingredients : [])
            setReviewNotes(draft.needsReview || [])
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Import failed')
        } finally {
            setImporting(false)
        }
    }

    const save = async () => {
        setSaving(true)
        setError('')
        try {
            const saved = editing && id
                ? await updateRecipe(id, recipe)
                : await createRecipe(recipe)
            navigate(`/recipe/${saved.id}`)
        } catch (e: unknown) {
            if (e instanceof ApiError && e.status === 403) {
                setError('You can only edit recipes you created.')
            } else {
                setError(e instanceof Error ? e.message : 'Could not save recipe')
            }
            setSaving(false)
        }
    }

    const [confirm, confirmDialog] = useConfirm()

    const remove = async () => {
        if (!id) return
        const ok = await confirm({
            title: 'Delete Recipe',
            body: <>Delete <strong>{recipe.title}</strong>? This cannot be undone.</>,
            confirmLabel: 'Delete',
        })
        if (!ok) return
        setSaving(true)
        try {
            await deleteRecipe(id)
            navigate('/')
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Could not delete recipe')
            setSaving(false)
        }
    }

    // --- ingredient/step/equipment list editing -------------------------

    // setInfoAt records what a row's mass was arrived at, without touching the
    // mass. That split is the point: a conversion the tables could not make must
    // leave the author's own figure alone and only say why.
    const setInfoAt = (i: number, info: MassInfo | null) =>
        setMassInfo(prev => {
            const next = prev.slice()
            while (next.length <= i) next.push(null)
            next[i] = info
            return next
        })

    // Editing an ingredient makes whatever was said about its mass wrong, so the
    // account goes with the edit — but only that row's. The other ingredients on
    // screen are untouched, and their basis is what an author is reading to
    // decide which ones need a scale. A conversion is the one edit that arrives
    // with an account of itself, and passes it in.
    //
    // It patches from the previous state rather than the rendered recipe because
    // a conversion lands after a round trip to the server, and whatever else was
    // typed in the meantime has to survive it.
    const patchIngredient = (i: number, patch: Partial<Ingredient>, info: MassInfo | null = null) => {
        setInfoAt(i, info)
        setRecipe(prev => ({
            ...prev,
            ingredients: prev.ingredients.map((ing, n) => (n === i ? { ...ing, ...patch } : ing)),
        }))
    }
    // A note says nothing about the mass, so editing one must not discard the
    // account of where that mass came from — which patchIngredient does, and
    // rightly, for the edits that can invalidate it.
    const setIngredientNote = (i: number, notes: string) =>
        set('ingredients', recipe.ingredients.map(
            (ing, n) => (n === i ? { ...ing, notes: notes || undefined } : ing)))

    const addIngredient = () => {
        setMassInfo(prev => [...prev, null])
        set('ingredients', [...recipe.ingredients, { name: '', massG: 0 }])
    }
    // Deleting an ingredient renumbers every one after it, and the steps hold
    // those numbers. Both lists move in the one update: leaving the steps to be
    // fixed on the next save would mean a step pointing at whatever slid into
    // the gap — the amounts would still render, against the wrong food.
    const removeIngredient = (i: number) => {
        setMassInfo(prev => prev.filter((_, n) => n !== i))
        setRecipe(prev => ({
            ...prev,
            ingredients: prev.ingredients.filter((_, n) => n !== i),
            steps: prev.steps.map(s => ({
                ...s,
                uses: (s.uses ?? [])
                    .filter(u => u.ingredient !== i)
                    .map(u => (u.ingredient > i ? { ...u, ingredient: u.ingredient - 1 } : u)),
            })),
        }))
    }

    // settleQuantity turns what was typed into the mass the recipe stores.
    //
    // Most of it never leaves the browser: a bare number is already grams, and
    // g / kg / oz / lb are arithmetic that is true whatever the food is. A volume
    // or a count is not — "1 cup" of flour and of water differ by half again — so
    // those go to the server, which has the density and per-item tables and says
    // which row it used.
    //
    // The case worth being careful about is the one where that lookup finds
    // nothing. It must not write a zero: the figure already in the form is the
    // author's, and it is worth more than "no mass". It stays, and the note says
    // what was missing.
    const settleQuantity = async (i: number, text: string) => {
        const typed = text.trim()
        if (typed === '') {
            // Emptying the field says "not weighed yet", which is the 0 g the
            // rest of the page already knows how to show.
            patchIngredient(i, { massG: 0 })
            return
        }

        const grams = parseMass(typed)
        if (grams !== null) {
            const mass = roundGrams(grams)
            // A bare number was grams already and there is nothing to account
            // for. A unit was converted, and since the field is about to stop
            // showing what was typed, it says what it did with it.
            const converted = !/^\d*\.?\d+$/.test(typed)
            patchIngredient(i, { massG: mass },
                converted ? { confidence: 'exact', note: `${typed} = ${gramsLabel(mass)}` } : null)
            return
        }

        // Which food it is decides what a cup or a clove of it weighs, so the
        // ingredient has to be named before the tables can be asked.
        const name = recipe.ingredients[i]?.name.trim() ?? ''
        if (!name) {
            setInfoAt(i, {
                confidence: 'unknown',
                note: `"${typed}" needs to know which ingredient it is of — name it first, or type a weight.`,
            })
            return
        }
        try {
            const measured = await measureQuantity(typed, name)
            if (measured.massG > 0) {
                patchIngredient(i, { massG: measured.massG }, measured)
            } else {
                setInfoAt(i, measured)
            }
        } catch (e: unknown) {
            setInfoAt(i, {
                confidence: 'unknown',
                note: e instanceof Error ? e.message : `could not work out what "${typed}" weighs`,
            })
        }
    }

    const patchStep = (i: number, patch: Partial<Step>) =>
        set('steps', recipe.steps.map((s, n) => (n === i ? { ...s, ...patch } : s)))
    const addStep = () =>
        set('steps', [...recipe.steps, { number: recipe.steps.length + 1, instruction: '' }])
    const removeStep = (i: number) =>
        set('steps', recipe.steps.filter((_, n) => n !== i))

    // --- what each step takes off the ingredient list --------------------

    // claimed is how much of one ingredient the whole method already spends.
    // The share a step is given is measured against what is left of it, and an
    // ingredient the steps spend more than once is a recipe that contradicts
    // itself — worth saying, and not worth silently correcting.
    const claimed = (ingredient: number) => recipe.steps.reduce(
        (total, s) => total + (s.uses ?? [])
            .filter(u => u.ingredient === ingredient)
            .reduce((n, u) => n + u.portion, 0),
        0)

    const setUses = (i: number, uses: StepUse[]) => patchStep(i, { uses })

    const addUse = (i: number, ingredient: number) => {
        // Whatever the method has not spent yet, which for the usual case — an
        // ingredient that goes in whole, in one step — is all of it.
        const left = 1 - claimed(ingredient)
        setUses(i, [...(recipe.steps[i].uses ?? []),
            { ingredient, portion: left > 0.001 ? Number(left.toFixed(4)) : 1 }])
    }
    const removeUse = (i: number, ingredient: number) =>
        setUses(i, (recipe.steps[i].uses ?? []).filter(u => u.ingredient !== ingredient))
    const setUsePortion = (i: number, ingredient: number, portion: number) =>
        setUses(i, (recipe.steps[i].uses ?? [])
            .map(u => (u.ingredient === ingredient ? { ...u, portion } : u)))

    // Working out which step uses how much of what is the one figure on this
    // site a model produces on its own, so it arrives here — in the form, for
    // the author to check — rather than being saved from under them.
    const runAllocate = async () => {
        setAllocating(true)
        setError('')
        try {
            const got = await allocateStepUses(recipe.ingredients, recipe.steps)
            set('steps', recipe.steps.map((s, i) => ({ ...s, uses: got.steps[i]?.uses ?? [] })))
            setAmountNotes(got.needsReview ?? [])
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Could not work out the step amounts')
        } finally {
            setAllocating(false)
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading…</span>
                </Spinner>
            </div>
        )
    }

    // The "where from" column earns its width once anything on screen has an
    // account of itself — an import, or a quantity that had to be converted.
    const provenance = massInfo.some(Boolean)

    // Whether this recipe says anything at all about what its steps take, and
    // where it says something contradictory. All three are worth nothing until
    // the first amount exists, so they are read together.
    const anyStepAmounts = recipe.steps.some(s => (s.uses ?? []).length > 0)
    const shares = recipe.ingredients.map((ing, n) => ({ ing, n, share: claimed(n) }))
    const overSpent = shares.filter(s => s.share > 1.0001)
    const unspent = shares.filter(s => s.share <= 0.0001)

    return (
        <div>
            <Button
                variant="link"
                size="sm"
                className="p-0 mb-3 d-flex align-items-center gap-1"
                onClick={() => navigate(editing && id ? `/recipe/${id}` : '/')}
            >
                <BsArrowLeft /> Back
            </Button>

            {error && <Alert variant="danger">{error}</Alert>}

            {reviewNotes.length > 0 && (
                <Alert variant="warning">
                    <Alert.Heading className="fs-6">
                        {importMethod === 'language-model'
                            ? 'A model read this page — check these before saving'
                            : 'Imported — check these before saving'}
                    </Alert.Heading>
                    <ul className="mb-0 small">
                        {reviewNotes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                </Alert>
            )}

            {/* Import and the title/summary fields are both short, so they are
                peers on the intrinsic grid (C-LAYOUT-3) rather than two
                full-width bands with a lot of empty room to the right. */}
            <div className="card-grid mb-4">
                {!editing && (
                    <Card>
                        <Card.Header className="d-flex align-items-center gap-2">
                            <BsDownload /> Import From a URL
                        </Card.Header>
                        <Card.Body>
                            <p className="small text-body-secondary mb-2 prose-column">
                                Paste a link to a recipe. Pages that publish machine-readable
                                recipe data have their quantities read straight out of it. For the
                                rest — a blog post, a forum reply — the page is stripped to its
                                text and read by a language model, which works out the title, the
                                summary, the ingredients and the method. Every ingredient is then
                                converted to grams from published density tables, not by the
                                model. Either way the method is rewritten to one instruction per
                                step, and each step is given the amounts it takes.
                            </p>
                            <Text
                                type="url"
                                placeholder="https://example.com/some-recipe"
                                value={importUrl}
                                onChange={e => setImportUrl(e.target.value)}
                                style={{ width: '100%' }}
                            />
                            {/* Worth offering because "the page has recipe data" and "that
                                data is any good" are different things: a thin import is
                                usually a page whose markup carries an SEO blurb and half a
                                method. */}
                            <Form.Check
                                type="checkbox"
                                id="use-model"
                                className="small mt-2 text-body-secondary"
                                label="Always use the language model, even if the page publishes recipe data"
                                checked={useModel}
                                onChange={e => setUseModel(e.target.checked)}
                            />
                        </Card.Body>
                        <Card.Footer className="d-flex align-items-center justify-content-between">
                            <span className="small text-body-secondary">
                                {importMethod === 'structured-data'
                                    && 'Quantities read from the page\'s own recipe data; method rewritten.'}
                                {importMethod === 'language-model' && 'Read by a language model.'}
                            </span>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={runImport}
                                disabled={importing || !importUrl.trim()}
                                className="d-flex align-items-center gap-2"
                            >
                                {importing
                                    ? <><Spinner animation="border" size="sm" /> Reading…</>
                                    : <><BsDownload /> Import</>}
                            </Button>
                        </Card.Footer>
                    </Card>
                )}

                <Card>
                    <Card.Header>{editing ? 'Edit Recipe' : 'New Recipe'}</Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="r-title" className="small text-body-secondary mb-1">Title</Form.Label>
                            <Text id="r-title" value={recipe.title}
                                onChange={e => set('title', e.target.value)} style={{ width: '100%' }} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="r-subtitle" className="small text-body-secondary mb-1">Summary</Form.Label>
                            <Form.Control as="textarea" id="r-subtitle" rows={2} value={recipe.subtitle}
                                onChange={e => set('subtitle', e.target.value)} />
                        </Form.Group>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Label htmlFor="r-category" className="small text-body-secondary mb-1">Category</Form.Label>
                                <Text id="r-category" value={recipe.category}
                                    onChange={e => set('category', e.target.value)} style={{ width: '100%' }} />
                            </Col>
                            <Col md={6}>
                                <Form.Label htmlFor="r-difficulty" className="small text-body-secondary mb-1">Difficulty</Form.Label>
                                <Dropdown id="r-difficulty" value={recipe.difficulty} style={{ width: '100%' }}
                                    onChange={e => set('difficulty', e.target.value as Difficulty)}
                                    options={difficulties.map(d => ({ value: d, label: d }))} />
                            </Col>
                            <Col md={6}>
                                <Form.Label htmlFor="r-yield" className="small text-body-secondary mb-1">Yield</Form.Label>
                                <Text id="r-yield" value={recipe.yieldDescription}
                                    onChange={e => set('yieldDescription', e.target.value)} style={{ width: '100%' }} />
                            </Col>
                            <Col md={2}>
                                <Form.Label htmlFor="r-prep" className="small text-body-secondary mb-1">Prep (min)</Form.Label>
                                <Form.Control type="number" id="r-prep" min={0} value={recipe.prepTimeMin || ''}
                                    onChange={e => set('prepTimeMin', Number(e.target.value) || 0)} />
                            </Col>
                            <Col md={2}>
                                <Form.Label htmlFor="r-cook" className="small text-body-secondary mb-1">Cook (min)</Form.Label>
                                <Form.Control type="number" id="r-cook" min={0} value={recipe.cookTimeMin || ''}
                                    onChange={e => set('cookTimeMin', Number(e.target.value) || 0)} />
                            </Col>
                            <Col md={2}>
                                <Form.Label htmlFor="r-total" className="small text-body-secondary mb-1">Total (min)</Form.Label>
                                <Form.Control type="number" id="r-total" min={0} value={recipe.totalTimeMin || ''}
                                    onChange={e => set('totalTimeMin', Number(e.target.value) || 0)} />
                            </Col>
                        </Row>
                    </Card.Body>
                    <Card.Footer className="text-end small text-body-secondary">
                        {editing ? `owned by ${recipe.ownerEmail}` : 'you will own this recipe'}
                    </Card.Footer>
                </Card>
            </div>

            {/* Same two tracks the recipe view uses, so editing a step happens
                next to the masses it refers to. */}
            <div className="recipe-workspace mb-4">
                <section className="recipe-ingredients">
                    <Card>
                        <Card.Header>Ingredients</Card.Header>
                        <Card.Body>
                            {/* The one thing about this column that is not obvious from
                                looking at it: it takes units, and it converts. */}
                            <p className="small text-body-secondary mb-2 prose-column">
                                A quantity can be in any unit — <code>250 g</code>, <code>8 oz</code>,{' '}
                                <code>1 1/2 cups</code>, <code>3 cloves</code>. Anything that is not
                                already a weight is converted using the same published density and
                                per-item tables an import uses, and the field settles to grams. A bare
                                number is grams.
                            </p>
                            <Table size="sm" className="ingredient-table m-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Ingredient</th>
                                        <th style={{ width: '9rem' }} className="text-end">Quantity</th>
                                        {/* Only once something has an account of itself.
                                            A mass reads the same whether USDA said so or a
                                            model guessed, and that is what decides which
                                            ones need a scale. */}
                                        {provenance && <th className="ps-3" style={{ width: '7rem' }}>Where from</th>}
                                        <th style={{ width: '5rem' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recipe.ingredients.map((ing, i) => {
                                        const info = massInfo[i]
                                        const badge = info ? confidenceBadge[info.confidence] ?? confidenceBadge.unknown : null
                                        return (
                                            <tr key={i}>
                                                <td>
                                                    <Text value={ing.name} placeholder="bread flour"
                                                        onChange={e => patchIngredient(i, { name: e.target.value })}
                                                        style={{ width: '100%' }} />
                                                    {/* The note the recipe carries, which the
                                                        view shows on hover rather than in a
                                                        column. An import fills it in with where
                                                        the mass came from; it is the author's
                                                        line to keep, rewrite or clear. */}
                                                    <Text value={ing.notes ?? ''}
                                                        placeholder="note — sifted, room temperature, 2 cups"
                                                        aria-label={`Note on ${ing.name || 'ingredient'}`}
                                                        onChange={e => setIngredientNote(i, e.target.value)}
                                                        style={{
                                                            width: '100%', marginTop: '0.25rem',
                                                            padding: '0.25rem 0.5rem', fontSize: '0.85rem',
                                                        }} />
                                                    {/* What the mass beside it was arrived at. An
                                                        import writes its own note onto the
                                                        ingredient, so only say it once. `unknown`
                                                        means the mass on screen is still the
                                                        author's to supply, which is a warning and
                                                        not a footnote. */}
                                                    {info?.note && info.note !== ing.notes && (
                                                        <div className={`small mt-1 ${info.confidence === 'unknown' ? 'text-danger' : 'text-body-secondary'}`}>
                                                            {info.note}
                                                        </div>
                                                    )}
                                                    {info?.basis && (
                                                        <div className="text-body-secondary" style={{ fontSize: '0.75rem' }}>
                                                            {info.basis}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <QuantityField
                                                        massG={ing.massG}
                                                        label={`Quantity of ${ing.name || 'ingredient'}`}
                                                        onSettle={text => settleQuantity(i, text)}
                                                    />
                                                </td>
                                                {provenance && (
                                                    <td className="ps-3">
                                                        {badge && <Badge bg={badge.bg} title={badge.hint}>{badge.label}</Badge>}
                                                    </td>
                                                )}
                                                <td className="text-end">
                                                    <Button variant="link" size="sm" className="p-0 text-danger"
                                                        aria-label={`Remove ${ing.name || 'ingredient'}`}
                                                        onClick={() => removeIngredient(i)}>
                                                        <BsX size={20} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    {recipe.ingredients.length === 0 && (
                                        <tr><td colSpan={provenance ? 4 : 3} className="text-body-secondary small">No ingredients yet.</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                        <Card.Footer className="d-flex justify-content-between align-items-center">
                            <Button variant="link" size="sm" className="p-0 d-flex align-items-center gap-1"
                                onClick={addIngredient}>
                                <BsPlusLg /> Add Ingredient
                            </Button>
                            <span className="small text-body-secondary">
                                total <span className="mass-value">{gramsLabel(totalMass(recipe))}</span>
                            </span>
                        </Card.Footer>
                    </Card>
                </section>

                <section className="recipe-procedure">
                    <Card>
                        <Card.Header>Procedure</Card.Header>
                        <Card.Body>
                            {/* The one thing about a step that is not obvious from the
                                box you type it into: it is shown as a list, not a
                                paragraph, and where it breaks is something you can
                                decide rather than something inferred from your
                                punctuation. */}
                            <p className="small text-body-secondary mb-2 prose-column">
                                Each step reads as a bulleted list. Put each action on its own
                                line to say where the bullets go; a step written as one block is
                                split at sentence ends.
                            </p>
                            {amountNotes.length > 0 && (
                                <Alert variant="warning" className="small py-2">
                                    <ul className="mb-0 ps-3">
                                        {amountNotes.map((n, i) => <li key={i}>{n}</li>)}
                                    </ul>
                                </Alert>
                            )}
                            <Stack gap={3}>
                                {recipe.steps.map((s, i) => (
                                    <div key={i}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="small text-body-secondary">Step {i + 1}</span>
                                            <Button variant="link" size="sm" className="p-0 text-danger"
                                                aria-label={`Remove step ${i + 1}`} onClick={() => removeStep(i)}>
                                                <BsX size={20} />
                                            </Button>
                                        </div>
                                        <Form.Control as="textarea" rows={2} value={s.instruction}
                                            placeholder="Combine the flour and water…"
                                            onChange={e => patchStep(i, { instruction: e.target.value })} />
                                        <Row className="g-2 mt-1">
                                            <Col xs={6}>
                                                <Form.Control type="number" min={0} placeholder="duration (s)"
                                                    value={s.durationSec ?? ''}
                                                    onChange={e => patchStep(i, { durationSec: Number(e.target.value) || undefined })} />
                                            </Col>
                                            <Col xs={6}>
                                                <Form.Control type="number" min={0} placeholder="temp (°C)"
                                                    value={s.temperatureC ?? ''}
                                                    onChange={e => patchStep(i, { temperatureC: Number(e.target.value) || undefined })} />
                                            </Col>
                                        </Row>
                                        {/* What the step takes off the ingredient list.
                                            The reader sees these above the step, and
                                            they are what carries the recipe's amounts
                                            into the method — a mass typed into the
                                            sentence cannot be scaled. */}
                                        <div className="step-uses mt-2">
                                            {(s.uses ?? []).map(u => {
                                                const ing = recipe.ingredients[u.ingredient]
                                                if (!ing) return null
                                                return (
                                                    <span className="step-use" key={u.ingredient}>
                                                        <span className="step-use-name">
                                                            {ing.name || <span className="fst-italic">unnamed</span>}
                                                        </span>
                                                        <StepUseField
                                                            totalG={ing.massG}
                                                            portion={u.portion}
                                                            label={`${ing.name || 'ingredient'} used in step ${i + 1}`}
                                                            onSettle={p => setUsePortion(i, u.ingredient, p)}
                                                        />
                                                        <Button variant="link" size="sm" className="p-0 text-danger lh-1"
                                                            aria-label={`Step ${i + 1} does not use ${ing.name || 'this ingredient'}`}
                                                            onClick={() => removeUse(i, u.ingredient)}>
                                                            <BsX size={18} />
                                                        </Button>
                                                    </span>
                                                )
                                            })}
                                            {/* Only ingredients this step does not already
                                                name: one step takes one amount of a thing. */}
                                            {recipe.ingredients.some((_, n) =>
                                                !(s.uses ?? []).some(u => u.ingredient === n)) && (
                                                <Dropdown
                                                    value=""
                                                    aria-label={`Add an ingredient to step ${i + 1}`}
                                                    onChange={e => e.target.value !== ''
                                                        && addUse(i, Number(e.target.value))}
                                                    options={[
                                                        { value: '', label: '+ uses…' },
                                                        ...recipe.ingredients
                                                            .map((ing, n) => ({ ing, n }))
                                                            .filter(({ n }) => !(s.uses ?? []).some(u => u.ingredient === n))
                                                            .map(({ ing, n }) => ({
                                                                value: String(n),
                                                                label: ing.name || `ingredient ${n + 1}`,
                                                            })),
                                                    ]}
                                                    style={{ padding: '0.1rem 1.6rem 0.1rem 0.4rem', fontSize: '0.9rem' }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {recipe.steps.length === 0 && (
                                    <p className="text-body-secondary small mb-0">No steps yet.</p>
                                )}
                            </Stack>
                            {/* The two ways the amounts can contradict the ingredient
                                list, checked continuously rather than at save: an
                                author moving shares between steps is exactly when
                                either one appears, and both are arithmetic the page
                                can do for them. */}
                            {anyStepAmounts && (overSpent.length > 0 || unspent.length > 0) && (
                                <div className="small mt-3">
                                    {overSpent.map(({ ing, n, share }) => (
                                        <div key={`over-${n}`} className="text-warning">
                                            The steps use {formatGrams(ing.massG * share)} g of{' '}
                                            {ing.name || `ingredient ${n + 1}`} — more than the{' '}
                                            {gramsLabel(ing.massG)} the recipe lists.
                                        </div>
                                    ))}
                                    {unspent.length > 0 && (
                                        <div className="text-body-secondary">
                                            No step uses {unspent.map(u => u.ing.name || `ingredient ${u.n + 1}`).join(', ')}.
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                        <Card.Footer className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                            <Button variant="link" size="sm" className="p-0 d-flex align-items-center gap-1"
                                onClick={addStep}>
                                <BsPlusLg /> Add Step
                            </Button>
                            {/* Reading a method and saying which step takes how much of
                                what is the one thing here with no table behind it, so it
                                is a model's answer, offered rather than applied: it
                                lands in the form and is saved only when the author
                                saves. */}
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={runAllocate}
                                disabled={allocating || recipe.ingredients.length === 0 || recipe.steps.length === 0}
                                title={'Read the method and work out how much of each ingredient every '
                                    + 'step takes. Replaces the amounts already on the steps; nothing is '
                                    + 'saved until you save.'}
                                className="d-flex align-items-center gap-2"
                            >
                                {allocating
                                    ? <><Spinner animation="border" size="sm" /> Reading the method…</>
                                    : <><BsMagic /> {anyStepAmounts ? 'Redo Step Amounts' : 'Work Out Step Amounts'}</>}
                            </Button>
                        </Card.Footer>
                    </Card>
                </section>
            </div>

            {/* Save stays a full-width band under both tracks: it acts on the
                whole form, so it should not read as belonging to one column. */}
            <Card>
                <Card.Header>{editing ? 'Save Changes' : 'Save Recipe'}</Card.Header>
                <Card.Body>
                    <Table size="sm" borderless className="m-0">
                        <tbody>
                            <tr>
                                <td className="text-body-secondary" style={{ width: '40%' }}>Ingredients</td>
                                <td className="fw-bold">{recipe.ingredients.length}</td>
                            </tr>
                            <tr>
                                <td className="text-body-secondary">Steps</td>
                                <td className="fw-bold">{recipe.steps.length}</td>
                            </tr>
                            <tr>
                                <td className="text-body-secondary">Total Mass</td>
                                <td className="fw-bold mass-value">{gramsLabel(totalMass(recipe))}</td>
                            </tr>
                        </tbody>
                    </Table>
                </Card.Body>
                <Card.Footer className="d-flex justify-content-between align-items-center">
                    {editing ? (
                        <Button variant="outline-danger" size="sm" onClick={remove} disabled={saving}
                            className="d-flex align-items-center gap-2">
                            <BsTrash /> Delete
                        </Button>
                    ) : <span />}
                    <Button variant="primary" onClick={save} disabled={saving || !recipe.title.trim()}
                        className="d-flex align-items-center gap-2">
                        <BsSave /> {saving ? 'Saving…' : 'Save Recipe'}
                    </Button>
                </Card.Footer>
            </Card>

            {confirmDialog}
        </div>
    )
}

export default RecipeEditPage
